import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { visibleServices, getServiceBySlug } from "../data/services";
import useReveal from "../hooks/useReveal";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function ServiceDetail() {
  useReveal();
  const { slug } = useParams();
  const item = getServiceBySlug(slug);

  if (!item) {
    return (
      <main className="min-h-screen bg-[#F7F5F0] text-[#1C1C19] flex flex-col">
        <Navbar />
        <section className="flex-1 flex items-center justify-center px-6 py-40">
          <div className="text-center" data-testid="service-not-found">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5C58]">
              404 · Not a practice
            </span>
            <h1 className="mt-6 font-display text-5xl md:text-7xl font-light tracking-tight">
              That practice <span className="italic">doesn't exist.</span>
            </h1>
            <Link
              to="/"
              data-testid="back-home-link"
              className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1C1C19] text-[#F7F5F0]"
            >
              <ArrowLeft size={16} />
              Back home
            </Link>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  const nav = item.hidden
    ? [item, ...visibleServices]
    : visibleServices;
  const idx = nav.findIndex((s) => s.slug === item.slug);
  const next = nav[(idx + 1) % nav.length];
  const isComing = !!item.comingSoon;

  return (
    <main className="min-h-screen bg-[#F7F5F0] text-[#1C1C19] overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section
        data-testid="service-detail-hero"
        className="relative pt-32 md:pt-40 pb-24 md:pb-32"
        style={{ background: item.bg, color: item.fg }}
      >
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12">
          <Link
            to="/#services"
            data-testid="back-to-services"
            className="link-underline inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] opacity-80"
          >
            <ArrowLeft size={14} /> All practices
          </Link>

          <div className="mt-10 grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold uppercase tracking-[0.25em] opacity-80">
                  Practice
                </span>
                {isComing && (
                  <span
                    data-testid="service-detail-coming-soon"
                    className="text-[10px] font-bold uppercase tracking-[0.25em] bg-[#1C1C19] text-[#F7F5F0] rounded-full px-3 py-1"
                  >
                    Coming Soon
                  </span>
                )}
              </div>
              <h1
                data-testid="service-detail-title"
                className="mt-6 font-display text-5xl sm:text-6xl lg:text-[8rem] font-light tracking-tight leading-[0.95]"
              >
                {item.title}
              </h1>
              {item.tagline && (
                <p className="mt-8 max-w-2xl text-xl md:text-2xl font-light italic font-display opacity-95">
                  {item.tagline}
                </p>
              )}
            </div>
            <div className="md:col-span-4">
              <p className="text-base md:text-lg font-light leading-relaxed opacity-95">
                {item.summary}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cover image */}
      {item.image && (
        <section className="relative -mt-12 md:-mt-20">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
            <div className="aspect-[16/7] rounded-3xl overflow-hidden bg-[#E6E3DB] shadow-2xl">
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-full object-cover"
                data-testid="service-detail-image"
              />
            </div>
          </div>
        </section>
      )}

      {/* Coming-soon body OR offerings + process */}
      {isComing ? (
        <section className="py-24 md:py-32" data-testid="service-coming-soon-body">
          <div className="max-w-screen-2xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
            <div className="md:col-span-3 reveal">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5C58]">
                (01) · What we're building
              </span>
              <div className="mt-6 w-12 h-px bg-[#1C1C19]" />
            </div>
            <div className="md:col-span-9 reveal space-y-8 max-w-4xl">
              {item.body.map((para, i) => (
                <p
                  key={para}
                  data-testid={`service-coming-soon-paragraph-${i + 1}`}
                  className="font-display text-2xl md:text-4xl font-light leading-[1.25] tracking-tight text-[#1C1C19]"
                >
                  {para}
                </p>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* Offerings */}
          <section className="py-24 md:py-32">
            <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
              <div className="grid md:grid-cols-12 gap-12 mb-16">
                <div className="md:col-span-3 reveal">
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5C58]">
                    (01) · What we do
                  </span>
                  <div className="mt-6 w-12 h-px bg-[#1C1C19]" />
                </div>
                <h2 className="md:col-span-9 reveal font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
                  Ways we
                  <br />{" "}
                  <span className="italic" style={{ color: item.bg }}>
                    show up.
                  </span>
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {item.offerings.map((o, i) => (
                  <article
                    key={o.title}
                    data-testid={`offering-${i + 1}`}
                    className="reveal group relative p-8 md:p-12 rounded-3xl border border-[#D8D4C7] bg-[#F7F5F0] hover:bg-white transition-all duration-500 hover:-translate-y-1 hover:shadow-xl"
                  >
                    <div className="flex items-start justify-between">
                      <span className="font-display italic text-xl text-[#5C5C58]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ background: item.bg }}
                      />
                    </div>
                    <h3 className="mt-10 font-display text-3xl md:text-4xl font-light tracking-tight">
                      {o.title}
                    </h3>
                    {o.body && (
                      <p className="mt-4 text-base font-light leading-relaxed text-[#1C1C19]/80">
                        {o.body}
                      </p>
                    )}
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Process */}
          {item.process && item.process.length > 0 && (
          <section className="py-24 md:py-32 bg-[#1C1C19] text-[#F7F5F0]">
            <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
              <div className="grid md:grid-cols-12 gap-12 mb-16">
                <div className="md:col-span-3 reveal">
                  <span
                    className="text-xs font-bold uppercase tracking-[0.25em]"
                    style={{ color: item.accent }}
                  >
                    (02) · How we work
                  </span>
                  <div
                    className="mt-6 w-12 h-px"
                    style={{ background: item.accent }}
                  />
                </div>
                <h2 className="md:col-span-9 reveal font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
                  A process built
                  <br />
                  <span className="italic" style={{ color: item.accent }}>
                    for the long run.
                  </span>
                </h2>
              </div>

              <div className="grid md:grid-cols-4 gap-px bg-[#F7F5F0]/10 rounded-3xl overflow-hidden">
                {item.process.map((p, i) => (
                  <div
                    key={p.step}
                    data-testid={`process-step-${i + 1}`}
                    className="bg-[#1C1C19] p-8 md:p-10 reveal"
                  >
                    <div
                      className="font-display text-5xl md:text-6xl font-light"
                      style={{ color: item.accent }}
                    >
                      {p.step}
                    </div>
                    <h3 className="mt-6 font-display text-2xl md:text-3xl font-light">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm font-light text-[#F7F5F0]/80 leading-relaxed">
                      {p.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          )}
        </>
      )}

      {/* Next practice */}
      <section className="py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5C58]">
            Next practice
          </span>
          <Link
            to={`/services/${next.slug}`}
            data-testid="next-service-link"
            className="mt-6 group flex flex-col md:flex-row md:items-end md:justify-between gap-6"
          >
            <h3
              className="font-display text-5xl md:text-8xl font-light tracking-tight leading-[1] transition-colors duration-500"
              style={{ color: "#1C1C19" }}
            >
              {next.title}
            </h3>
            <span
              className="inline-flex items-center gap-3 px-6 py-3 rounded-full text-xs uppercase tracking-widest text-[#F7F5F0] group-hover:opacity-90 transition"
              style={{ background: next.bg === "#F2C14E" ? "#1C1C19" : next.bg }}
            >
              Explore
              <ArrowRight size={14} />
            </span>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-24 md:py-32 relative overflow-hidden"
        style={{ background: item.bg, color: item.fg }}
      >
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-4xl md:text-7xl font-light tracking-tight leading-[1.05]">
            {isComing ? (
              <>
                Interested in
                <br /> <span className="italic">what this becomes?</span>
              </>
            ) : (
              <>
                Ready to <span className="italic">put down roots?</span>
              </>
            )}
          </h2>
          <Link
            to="/#contact"
            data-testid="service-cta"
            className="btn-magnetic mt-10 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#1C1C19] text-[#F7F5F0] text-sm uppercase tracking-widest"
          >
            {item.cta}
            <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
